import { useState } from 'react';
import type { ThreadTopicSummary } from '@shared/types/thread-work';
import { Button } from '@/components/ui';
import { GuidanceMotifItems } from '@/components/activity/GuidanceMotifItems';
import { planTopic } from '@/lib/topic-plan';
import { useKeyShortcut } from '@/lib/use-key-shortcut';
import type { WorkGuidanceMotif } from '@/lib/work-guidance';
import { usePopover } from './usePopover';

/**
 * « Écarter » one next action of an object, with its motive (J6): kept as
 * guidance for the planner. « Déjà fait » closes it as done.
 */
export const AppRecordSetAside = ({ topic, onDone, hotkey }: {
  topic: ThreadTopicSummary;
  onDone: () => void;
  /** In a séance, the key that opens the motives. */
  hotkey?: string;
}) => {
  const { open, setOpen, ref } = usePopover();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useKeyShortcut(hotkey ?? '', () => setOpen(true), Boolean(hotkey));

  const pick = async (motif: WorkGuidanceMotif, note: string | null) => {
    setPending(true);
    setError(null);
    try {
      await planTopic({ topicId: topic.id, expectedTopicVersion: topic.version, horizon: motif === 'done' ? 'done' : 'dropped', motif, note });
      setOpen(false);
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Cette action n’a pas pu être écartée.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <Button variant="secondary" aria-expanded={open} onClick={() => setOpen(!open)}
        title={hotkey ? `Raccourci : ${hotkey.toUpperCase()}` : undefined}>
        Écarter
      </Button>
      {open && (
        <div role="menu" aria-label={`Écarter « ${topic.title} »`} className="absolute left-0 top-11 z-20 w-56 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]">
          <GuidanceMotifItems disabled={pending} onPick={(motif, note) => void pick(motif, note)} />
        </div>
      )}
      {error && <p role="alert" className="mt-1 text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
